/**
 * AuthModal.jsx — sign-in window.
 *
 * Telegram: polling flow — site calls /auth/telegram/init → shows bot deep-link
 *           → user opens bot + sends /start → bot resolves state → polling detects.
 * Google:   OAuth popup flow — opens Google sign-in popup, receives id_token via postMessage
 *           from /google-callback.html.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api, getToken } from '../api/client';
import { TELEGRAM_BOT_USERNAME, GOOGLE_CLIENT_ID } from '../config';
import { t, useI18n } from '../hooks/useI18n';
import { desktopAuthHandoff } from '../utils/desktopAuthHandoff';
import { tr } from '@quantum/ui';

const T = {
  panelBorder: 'rgba(180,200,230,0.07)',
  textPrimary: 'rgba(230,236,246,0.96)',
  textSecondary: 'rgba(210,220,236,0.58)',
  textTertiary: 'rgba(210,220,236,0.34)',
  textQuiet: 'rgba(210,220,236,0.20)',
  up: '#2CF5A7',
  fontMono: '"JetBrains Mono", monospace',
};

function TelegramIcon({ small }) {
  const s = small ? 14 : 18;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" fill="#5cb6ff" opacity="0.15" />
      <path d="M7 12.5 L17 7 L15 18 L10.5 14.5 L8.5 16 L8.5 13 L17 7" stroke="#5cb6ff" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" fill="none" />
    </svg>
  );
}
function GmailIcon({ small }) {
  const s = small ? 14 : 18;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="6" width="18" height="13" rx="2" stroke="#FF5C87" strokeWidth="1.4" fill="#FF5C87" fillOpacity="0.10" />
      <path d="M3.5 7.5 L12 13 L20.5 7.5" stroke="#FF5C87" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    </svg>
  );
}
const loginBtnStyle = (c) => ({
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
  padding: '14px 18px', borderRadius: 12,
  background: `linear-gradient(180deg, ${c}22, ${c}08)`, border: `1px solid ${c}45`,
  color: T.textPrimary, fontFamily: "'Inter', system-ui, -apple-system, sans-serif", fontSize: 13.5, fontWeight: 500,
  letterSpacing: 0.4, cursor: 'pointer', transition: 'all .22s ease',
});
// ── Telegram polling sub-component ────────────────────────────────────────────
function TelegramPolling({ onDone, setAuthResult }) {
  const [botUrl, setBotUrl] = useState(null);
  const [state, setState] = useState(null);
  const [status, setStatus] = useState('init'); // init | waiting | success | error
  const pollRef = useRef(null);

  useEffect(() => {
    // 1. Init auth state on the server
    api('/auth/telegram/init', { method: 'POST', auth: false })
      .then((data) => {
        setState(data.state);
        setBotUrl(data.botUrl);
        setStatus('waiting');
      })
      .catch(() => setStatus('error'));

    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    if (status !== 'waiting' || !state) return;

    // 2. Poll until the bot resolves the state. Every 3s (the auth endpoint is
    // rate-limited; the status GET is exempt server-side but a slow poll is
    // still the polite default). A 429 / network blip is ignored — only a
    // real 404 (state gone) or the 9-minute cap ends it.
    const started = Date.now();
    pollRef.current = setInterval(async () => {
      if (Date.now() - started > 9 * 60_000) {
        clearInterval(pollRef.current);
        setStatus('error');
        return;
      }
      try {
        const data = await api(`/auth/telegram/status?state=${state}`, { auth: false });
        if (data.status === 'ok') {
          clearInterval(pollRef.current);
          setStatus('success');
          setAuthResult({ token: data.token, user: data.user });
          onDone();
        }
      } catch (e) {
        if (e.status === 404) {
          clearInterval(pollRef.current);
          setStatus('error');
        }
        // 429 / 5xx / offline — keep polling, the bot may still confirm.
      }
    }, 3000);

    return () => clearInterval(pollRef.current);
  }, [status, state, onDone, setAuthResult]);

  if (status === 'init') return (
    <div style={{ textAlign: 'center', color: T.textTertiary, fontFamily: T.fontMono, fontSize: 10.5 }}>
      Initializing…
    </div>
  );

  if (status === 'error') return (
    <div style={{ textAlign: 'center', color: '#FF5C87', fontFamily: T.fontMono, fontSize: 10.5 }}>
      {tr('Error. Please try again.')}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <a
        href={botUrl || `https://t.me/${TELEGRAM_BOT_USERNAME}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          width: '100%', padding: '13px 0', borderRadius: 11, textDecoration: 'none',
          background: 'linear-gradient(180deg,rgba(92,182,255,0.22),rgba(92,182,255,0.07))',
          border: '1px solid rgba(92,182,255,0.40)',
          color: T.textPrimary, fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
          fontSize: 13.5, fontWeight: 500, letterSpacing: 0.4,
        }}
      >
        <TelegramIcon />
        <span>Open @{TELEGRAM_BOT_USERNAME}</span>
      </a>

      {/* Waiting indicator */}
      {status === 'waiting' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.textTertiary, fontFamily: T.fontMono, fontSize: 9.5, letterSpacing: 0.6 }}>
          <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#5cb6ff', animation: 'pulse 1.4s ease-in-out infinite' }} />
          {tr('WAITING FOR BOT CONFIRMATION…')}
        </div>
      )}
    </div>
  );
}

// ── Google popup sub-component ────────────────────────────────────────────────
function GoogleLogin({ onDone, loginGoogle }) {
  const [status, setStatus] = useState('idle'); // idle | waiting | success | error
  const popupRef = useRef(null);
  const listenerRef = useRef(null);
  const timerRef = useRef(null);

  const cleanup = () => {
    if (listenerRef.current) { window.removeEventListener('message', listenerRef.current); listenerRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (popupRef.current && !popupRef.current.closed) { popupRef.current.close(); }
    popupRef.current = null;
  };

  useEffect(() => () => cleanup(), []);

  const handleOpen = () => {
    if (!GOOGLE_CLIENT_ID) { setStatus('error'); return; }
    setStatus('waiting');

    const nonce = Math.random().toString(36).slice(2);
    const redirectUri = `${window.location.origin}/google-callback.html`;
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'id_token',
      scope: 'openid email profile',
      nonce,
      prompt: 'select_account',
    });

    popupRef.current = window.open(
      `https://accounts.google.com/o/oauth2/v2/auth?${params}`,
      'google-auth',
      'width=500,height=600,left=300,top=100'
    );

    listenerRef.current = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'google_auth') return;
      cleanup();
      loginGoogle(event.data.credential)
        .then(() => { setStatus('success'); onDone(); })
        .catch(() => setStatus('error'));
    };
    window.addEventListener('message', listenerRef.current);

    timerRef.current = setInterval(() => {
      if (popupRef.current?.closed) {
        cleanup();
        setStatus((s) => s === 'waiting' ? 'idle' : s);
      }
    }, 500);
  };

  if (status === 'error') return (
    <div style={{ textAlign: 'center', color: '#FF5C87', fontFamily: T.fontMono, fontSize: 10.5 }}>
      {GOOGLE_CLIENT_ID ? tr('Error. Please try again.') : tr('Google OAuth is not configured.')}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <button
        onClick={status !== 'waiting' ? handleOpen : undefined}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          width: '100%', padding: '13px 0', borderRadius: 11,
          background: 'linear-gradient(180deg,rgba(255,92,135,0.22),rgba(255,92,135,0.07))',
          border: '1px solid rgba(255,92,135,0.40)',
          color: T.textPrimary, fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
          fontSize: 13.5, fontWeight: 500, letterSpacing: 0.4,
          cursor: status === 'waiting' ? 'default' : 'pointer',
          opacity: status === 'waiting' ? 0.7 : 1,
        }}
      >
        <GmailIcon />
        <span>{t('auth.continue_gm')}</span>
      </button>

      {status === 'waiting' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.textTertiary, fontFamily: T.fontMono, fontSize: 9.5, letterSpacing: 0.6 }}>
          <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#FF5C87', animation: 'pulse 1.4s ease-in-out infinite' }} />
          {tr('WAITING FOR EMAIL CONFIRMATION…')}
        </div>
      )}
    </div>
  );
}

// ── Main modal ─────────────────────────────────────────────────────────────────
export default function AuthModal({ onClose }) {
  useI18n();
  const { loginGoogle, setAuthResult, error } = useAuth();
  const [method, setMethod] = useState(null);

  // Desktop-auth handoff — see utils/desktopAuthHandoff.js for the full
  // story (also called from App.jsx for an already-signed-in visitor).
  const done = () => {
    if (desktopAuthHandoff(getToken())) return;
    onClose?.();
  };

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 100,
      background: 'radial-gradient(ellipse at top left, #1c2435 0%, #08090d 70%)',
      backdropFilter: 'blur(20px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'modalFadeIn .35s ease',
    }}>
      <div style={{ position: 'absolute', top: '20%', left: '30%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(44,245,167,0.18) 0%, transparent 70%)', pointerEvents: 'none', filter: 'blur(40px)' }} />
      <div style={{ position: 'absolute', bottom: '20%', right: '20%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(122,162,247,0.12) 0%, transparent 70%)', pointerEvents: 'none', filter: 'blur(40px)' }} />

      <div style={{
        position: 'relative', width: 460, padding: 36,
        background: 'linear-gradient(180deg, rgba(28,34,46,0.85), rgba(20,24,34,0.95))',
        border: `1px solid ${T.panelBorder}`, borderRadius: 18, backdropFilter: 'blur(20px)',
        boxShadow: '0 40px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(44,245,167,0.08), inset 0 1px 0 rgba(255,255,255,0.04)',
        animation: 'modalPop .4s var(--ease-out)',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 30 }}>
          {/* The real logo, from the one file that defines it. This used to be an
              inline hexagon — a copy of an older mark that nobody updated when the
              brand changed, so the login screen greeted users with a dead logo. */}
          <div style={{
            width: 56, height: 56, borderRadius: 16,
            background: 'linear-gradient(135deg, rgba(154,59,239,0.20), rgba(47,107,255,0.06))',
            border: '1px solid rgba(154,59,239,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 12px 30px rgba(154,59,239,0.18)',
          }}>
            <img src="/favicon.svg" width={30} height={30} alt="Quantum" />
          </div>
          <div style={{ textAlign: 'center', lineHeight: 1.3 }}>
            <div style={{ fontSize: 20, fontWeight: 500, color: T.textPrimary, letterSpacing: 0.4 }}>{t('auth.welcome')}</div>
            <div style={{ fontFamily: T.fontMono, fontSize: 11, color: T.textTertiary, letterSpacing: 0.8, marginTop: 4 }}>{tr('FREE DURING BETA · FULL ACCESS')}</div>
          </div>
        </div>

        {!method ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button onClick={() => setMethod('telegram')} className="hover-glow" style={loginBtnStyle('#5cb6ff')}><TelegramIcon /><span>{t('auth.continue_tg')}</span></button>
            <button onClick={() => setMethod('gmail')} className="hover-glow" style={loginBtnStyle('#FF5C87')}><GmailIcon /><span>{t('auth.continue_gm')}</span></button>
            {error && <div style={{ color: '#FF5C87', fontFamily: T.fontMono, fontSize: 10.5, textAlign: 'center' }}>{error}</div>}
            <div style={{ marginTop: 14, padding: '12px 14px', background: 'rgba(20,24,34,0.6)', border: `1px solid ${T.panelBorder}`, borderRadius: 10, fontFamily: T.fontMono, fontSize: 10, color: T.textTertiary, letterSpacing: 0.4, lineHeight: 1.6, textAlign: 'center' }}>
              {tr('By continuing, you agree to our')}{' '}
              <a
                href="/privacy.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: T.up, textDecoration: 'underline', textUnderlineOffset: 2 }}
              >{tr('privacy policy')}</a>.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'rgba(20,24,34,0.6)', border: `1px solid ${T.panelBorder}`, borderRadius: 10 }}>
              {method === 'telegram' ? <TelegramIcon small /> : <GmailIcon small />}
              <span style={{ fontSize: 12, color: T.textSecondary, letterSpacing: 0.3 }}>{tr('Signing in via {m}', { m: method === 'telegram' ? 'Telegram' : 'Gmail' })}</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setMethod(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: T.textTertiary, fontFamily: T.fontMono, fontSize: 9.5, letterSpacing: 0.6 }}>{t('auth.change')}</button>
            </div>

            {/* Telegram: polling flow */}
            {method === 'telegram' && (
              <TelegramPolling onDone={done} setAuthResult={setAuthResult} />
            )}

            {/* Google: popup flow */}
            {method === 'gmail' && (
              <GoogleLogin onDone={done} loginGoogle={loginGoogle} />
            )}
          </div>
        )}

        <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 16, background: 'transparent', border: 'none', color: T.textTertiary, fontSize: 18, cursor: 'pointer' }}>×</button>
      </div>
    </div>
  );
}
